import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAddItemByAccount } from '@hub/api/mutations';
import { useErrorSnackbar } from '@hub/features/purchase/hooks/useErrorSnackbar';
import { PagePath } from '@monorepo/client-types';
import { DesignIcon } from '@monorepo/nova-icons';
import { Button, Typography, useTheme } from '@monorepo/nova-ui';
import { CartStep, OptionalAssets } from '@monorepo/types';
import {
  StyledLoadingSpinner,
  StyledModalContent,
  StyledPaymentDialog,
  StyledRocketIcon,
  StyledStarsIcon,
  StyledTextGroup,
} from './payment-required-modal.styled';

export const PaymentRequiredModal = ({
  isOpen,
  onClose,
  accountId,
  amount,
}: {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  amount: number;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showError } = useErrorSnackbar();
  const { mutateAsync: addItemByAccount, isPending } = useAddItemByAccount();
  const { t } = useTranslation(['dashboardTemplates']);

  const handleClaimNow = async () => {
    try {
      await addItemByAccount({ accountId });
      navigate(
        `${PagePath.Purchase}?step=${CartStep.Billing}&asset=${OptionalAssets.QuickBuy}`,
      );
    } catch (err: any) {
      showError(err, err?.message || 'Failed to claim');
    }
  };

  return (
    <StyledPaymentDialog open={isOpen} onClose={onClose}>
      <StyledModalContent>
        <StyledRocketIcon>
          <DesignIcon
            iconName="Rocket"
            useMuiSvgIcon={false}
            props={{
              color: theme.palette.primary.main,
            }}
          />
        </StyledRocketIcon>
        <StyledStarsIcon>
          <DesignIcon
            iconName="ShootingStars"
            useMuiSvgIcon={false}
            props={{
              color: theme.palette.primary.main,
            }}
          />
        </StyledStarsIcon>
        <Typography variant="h3">
          {t('dashboardTemplates:payment_required.modal_title_line1')}
          <br />
          {t('dashboardTemplates:payment_required.modal_title_line2')}
        </Typography>
        <StyledTextGroup>
          <Typography variant="h6" color="primary.main">
            {t('dashboardTemplates:payment_required.modal_subtitle')}
          </Typography>
          <Typography variant="subtitle1">
            {t('dashboardTemplates:payment_required.modal_description', {
              amount,
            })}
          </Typography>
        </StyledTextGroup>
        <Button
          variant="primary"
          size="xl"
          onClick={handleClaimNow}
          disabled={isPending}
        >
          {t('dashboardTemplates:payment_required.modal_button')}
          {isPending && <StyledLoadingSpinner size={18} />}
        </Button>
      </StyledModalContent>
    </StyledPaymentDialog>
  );
};
